import React, { useState, useEffect, useMemo } from "react";
import "../../styles/SearchResultsRHS.css";
import TableComponent from "../../components/UI/TableComponent.tsx";
import {
  CLAIMS_AMOUNT_DISBURSED_HEADERS,
  CLAIMS_CLAIM_COUNT_HEADERS,
  CLAIMS_CLAIM_PROCESSED_HEADERS,
  CLAIMS_CLAIM_RADIO_HEADERS,
  CLAIMS_CUSTOMER_COUNT_HEADERS,
  CUSTOMER_COUNT_HEADERS,
  getCountryStateTableHeader,
  INSURED_AREA_HEADER,
  POLICIES_HEADERS,
  POSP_ARABLE_HEADERS,
  POSP_HEADERS,
  POSP_LOCATION,
  POSP_TARGET_HEADERS,
  PREMIUM_HEADERS,
  SUM_INSURED_HEADERS,
  getClaimsStateGridHeadersByLevel2,
  CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS,
  CLAIMS_STATE_GRID_HEADERS_PAID,
  getClaimsCountryHeadersByLevel2,
  getClaimsStateHeadersByLevel2,
  CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_CP,
  CLAIMS_STATE_GRID_HEADERS_CP,
  CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_ABNP,
  CLAIMS_STATE_GRID_HEADERS_ABNP,
  CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_LS,
  CLAIMS_STATE_GRID_HEADERS_LS,
} from "../../utils/TableHeaders.tsx";
import Map from "../../components/UI/Map.tsx";
import { useDispatch, useSelector } from "react-redux";
import {
  getGridLatLng,
  getSelectedGeometry,
  getStateData,
} from "../../services/MapApi.tsx";
import { ColumnHeaderType, OptionType } from "../../utils/Types.tsx";
import {
  ACTIVE_VIEW_STATE,
  COLOR,
  COMBINED_LEVELS_FILTER,
  AGGREGATED_TITLE,
  TABLE_SORT_ASC,
  COUNTRY,
  ACTIVE_VIEW_COUNTRY,
  MAP_VIEW,
  TABLE_SORT_DESC,
  ALL_PRODUCT,
  STATE_ARRAY,
  REAL_TIME_LOCATION,
  MARKER_TYPE,
  DATE_FORMAT,
} from "../../utils/Constants.tsx";
import {
  formatNumber,
  getDataWithKeyValue,
  numberFormatWithoutCurrency,
  numberFormatWithCurrency,
  sortListItems,
  parseStateData,
  getRequestParams,
  getValueKey,
  sumAll,
  aggregatePayoutByState,
} from "../../utils/Util.tsx";
import {
  handleStateGridLatLng,
  handleStateSelectedGeometry,
  handleStoreMarkers,
  updateLegendValue,
} from "../../redux/slices/mapApiSlice.ts";
import { handleCountryGridLatLng } from "../../redux/slices/countryApiSlice.ts";
import moment, { Moment } from "moment";
import { DateHourMinutePlayer } from "../../components/UI/DateHourMinutePlayer.tsx";
import {
  WeatherLayerDropdown,
  WeatherLayerItem,
} from "../../components/UI/WeatherLayerDropdown.tsx";

import { ReactComponent as IconRain } from "../../assets/images/precipitation.svg";
import { ReactComponent as IconWind } from "../../assets/images/wind.svg";
import { ReactComponent as IconCyclone } from "../../assets/images/cyclone.svg";
import { ReactComponent as IconTemp } from "../../assets/images/temperature.svg";
import { ReactComponent as IconHumidity } from "../../assets/images/humidity.svg";

// --- Loss Reserve helpers (component scope) ---
const isLossReserveL2 = (l2?: string) => {
  const s = String(l2 ?? "")
    .toLowerCase()
    .trim();
  return (
    s === "claim pending (before approval) - loss reserve" ||
    s === "loss reserve"
  );
};
// --- Loss Ratio helper (for disabling state clicks only) ---
const isLossRatioMetric = (levelOne?: string, levelTwo?: string): boolean => {
  return (
    String(levelOne ?? "").toLowerCase().trim() === "claims" &&
    String(levelTwo ?? "").toLowerCase().trim() ===
      "loss ratio(claim/premium)"
  );
};

const computeTotalReserveFromRows = (rows: any[]) => {
  if (!Array.isArray(rows)) return 0;
  return rows.reduce((sum, r) => {
    const arr = Array.isArray(r?.loss_reserve_values)
      ? r.loss_reserve_values
      : [];
    const rowTotal = arr.reduce((s: number, v: any) => s + (Number(v) || 0), 0);
    return sum + rowTotal;
  }, 0);
};
// --- end helpers ---

const Level2KeyMap: Record<string, string> = {
  "loss ratio(claim/premium)": "loss_ratio",
  "approved but not paid": "approved_not_paid",
  "claim pending (before approval) - loss reserve": "loss_reserve",
  "loss reserve": "loss_reserve",
  rejected: "rejected",
  repudiated: "repudiated",
  cwp: "cwp",
  "claim paid": "claim_paid",
};

const normalizeCountry = (c?: any): string | undefined => {
  if (!c) return undefined;
  if (typeof c === "object" && c !== null) {
    const code = c.code ?? c.value ?? c.iso2 ?? c.alpha2;
    if (code) return String(code).trim().toUpperCase();
    const name = c.name ?? c.label ?? c.text;
    if (name) {
      const k = String(name).trim().toLowerCase();
      if (k === "india") return "IN";
    }
    return undefined;
  }
  const k = String(c).trim().toLowerCase();
  if (k === "india" || k === "in") return "IN";
  return k.length === 2 ? k.toUpperCase() : undefined;
};

const getGridIdWiseData = (list: any[], levelOne: string, levelTwo: string) => {
  const result = list?.reduce((acc: Record<string, any>, item: any) => {
    acc[item.gridid] = { ...item, levelOne, levelTwo };
    return acc;
  }, {});
  return result;
};

const getStateIdWiseDataWithSum = (
  list: any[],
  keyName: string,
  valueName: string,
  levelOne: string,
  levelTwo: string,
) => {
  const result = list?.reduce((acc: Record<string, any>, item: any) => {
    const key = item[keyName];
    if (!acc[key]) {
      acc[key] = {
        [keyName]: key,
        total: 0,
        levelOne,
        levelTwo,
      };
    }
    if (valueName === "payout_amounts" || valueName === "loss_reserve_values") {
      acc[key].total += Number(sumAll(item[valueName]) ?? 0);
    } else {
      acc[key].total += Number(item[valueName] ?? 0);
    }
    return acc;
  }, {});

  if (result) {
    const formattedResultList = Object.entries(result).map(([, data]: any) => {
      return {
        [keyName]: data?.sid,
        total: data.total,
      };
    });
    const sortedArray = sortListItems(
      formattedResultList,
      "total",
      TABLE_SORT_DESC,
    );
    return {
      data: result,
      array: sortedArray,
      max: sortedArray?.[0]?.total ?? 0,
      min: sortedArray?.[sortedArray.length - 1]?.total ?? 0,
    };
  }
  return undefined;
};

interface SearchResultsRHSProps {
  selectedFilters: any;
  setGridId: (data: string) => void;
  gridId: string;
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
  distanceRange: Number;
  stateFilter: string;
}

const StateMap = ({
  selectedFilters,
  setGridId,
  gridId,
  isLoading,
  setIsLoading,
  distanceRange,
  stateFilter,
}: SearchResultsRHSProps) => {
  const [columnHeaders, setColumnHeaders] = useState<ColumnHeaderType[]>([]);
  const [tableTitle, setTableTitle] = useState("");
  const [mapClickedInfo, setMapClickedInfo] = useState<any>(null);
  const markersStore = useSelector((state: any) => state.map.markersStore);
  const [stateTableData, setStateTableData] = useState<any[] | null>(null);
  const [mapToolTip, setMapToolTip] = useState<any>(null);
  const [markerType, setMarkersType] = useState<string[]>([]);
  const [legendStyle, setLegendStyle] = useState<any>(null);
  const [aggregatedValue, setAggregatedValue] = useState<any>(null);
  const [aggregatedTitle, setAggregatedTitle] = useState<any>(null);
  const [averageCatchmentRatio, setAverageCatchmentRatio] = useState<any>(null);
  const [sortBy, setSortBy] = useState<string | null>();
  const [isGridViewSelected, setGridViewSelected] = useState(false);
  const [sortOrder, setSortOrder] = useState<string>(TABLE_SORT_ASC);
  const [forecastDate, setForecastDate] = useState<Moment>(moment());
  const [idWiseData, setIdWiseData] = useState<any>(null);
  const [selectedState, setSelectedState] = useState<any>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<any>(null);
  const [canShowToolTip, setCanShowToolTip] = useState(false);

  const [totalPayoutUI, setTotalPayoutUI] = useState<number | null>(null);
  // Note for cwp,rejected,repudiated
  const claimsNoteRaw = useMemo(() => {
    const l2 = String(selectedFilters?.levelTwo ?? "")
      .toLowerCase()
      .trim();
    if (l2 === "cwp") return "Note - For CWP, the default Amount Paid is ₹0";
    if (l2 === "repudiated")
      return "Note - For Repudiated, the default Amount Paid is ₹0";
    if (l2 === "rejected")
      return "Note - For Rejected, the default Amount Paid is ₹0";
    return null;
  }, [selectedFilters?.levelTwo]);

  // Show the note only after fetch completes (never while loading)
  const canShowClaimsNote = useMemo(() => {
    if (!claimsNoteRaw) return false;
    if (isLoading) return false;
    return stateTableData !== null;
  }, [claimsNoteRaw, isLoading, stateTableData]);

  const activeView = useSelector((state: any) => state?.filter?.activeView);
  const computedDate = useSelector((state: any) => state?.map?.computedDate);
  const dispatch: any = useDispatch();
  const statesWithId = useSelector(
    (state: any) => state?.filter?.statesDataWithId,
  );
  const districtsWithId = useSelector(
    (state: any) => state?.filter?.districtsDataWithId,
  );
  const { stateViewData, selectedGeometry, legendValues } = useSelector(
    (state: any) => state.map,
  );
  const productListWithId = useSelector(
    (state: any) => state?.filter?.products,
  );
  const seasonListWithId = useSelector((state: any) => state?.filter?.seasons);
  const gridLatLng = useSelector((state: any) => state?.map?.gridLatLng);
  const showCount = Boolean(selectedFilters?.levelOne);

  // console.log(stateViewData, "will print the whole response");
  // console.log(
  //   stateViewData?.aggregated?.total_payout,
  //   "stateViewData?.aggregated?.total_payout",
  // );

  const items: WeatherLayerItem[] = [
    { id: "precipitation", label: "Precipitation", icon: <IconRain /> },
    { id: "wind", label: "Wind", icon: <IconWind /> },
    { id: "cyclone", label: "Cyclone", icon: <IconCyclone /> },
    { id: "temperature", label: "Temperature", icon: <IconTemp /> },
    { id: "humidity", label: "Humidity", icon: <IconHumidity /> },
  ];
  const [selected, setSelected] = useState<string>("precipitation");

  const computeTotalPayoutFromRows = (rows: any[]) => {
    if (!Array.isArray(rows)) return 0;

    const levelTwo = String(selectedFilters?.levelTwo ?? "")
      .toLowerCase()
      .trim();

    return rows.reduce((sum, r) => {
      if (levelTwo === "approved but payment not initiated") {
        const np = Array.isArray(r?.claims?.claim_details)
          ? r.claims.claim_details.reduce(
              (s: number, d: any) => s + (Number(d?.net_payout) || 0),
              0,
            )
          : 0;
        return sum + (Number.isFinite(np) ? np : 0);
      }

      const paidFromArray = Array.isArray(r?.payout_amounts)
        ? r.payout_amounts.reduce(
            (s: number, v: any) => s + (Number(v) || 0),
            0,
          )
        : 0;

      const paid = Number(r?.amount_paid ?? paidFromArray ?? 0);
      const sanctioned = Number(
        r?.amount_sanctioned ?? r?.approved_amount ?? 0,
      );
      const outstanding = Number(r?.outstanding ?? 0);
      const toBePaid = Number(r?.amount_to_be_paid ?? 0);
      const computedOutstanding = Math.max(sanctioned - paid, 0);

      const amt =
        levelTwo === "approved but not paid"
          ? toBePaid || outstanding || computedOutstanding || 0
          : paidFromArray || paid || 0;

      return sum + (Number.isFinite(Number(amt)) ? Number(amt) : 0);
    }, 0);
  };

  useEffect(() => {
    dispatch(handleStoreMarkers({ marker: "state", res: STATE_ARRAY }));
    if (!markerType.includes("state")) {
      setMarkersType(["state"]);
    }
  }, []);
  useEffect(() => {
    if (mapToolTip?.gridid) {
      setGridId(mapToolTip.gridid);
    }
  }, [mapToolTip, setGridId]);

  useEffect(() => {
    if (mapClickedInfo?.gridid && canShowToolTip) {
      setMapToolTip({
        ...idWiseData?.[mapClickedInfo?.gridid],
        gridid: mapClickedInfo?.gridid,
      });
      dispatch(
        getSelectedGeometry(
          { lat: mapClickedInfo.lat, lng: mapClickedInfo.lng },
          activeView,
        ),
      );
    }
    if (stateViewData) {
      updateTableData(mapClickedInfo?.gridid, "gridid");
    }
  }, [mapClickedInfo]);

  useEffect(() => {
    if (!gridId) {
      // Clear any stale selection context that can keep the UI in grid scope
      setMapToolTip(null);
      setMapClickedInfo(null);
      dispatch(handleStateSelectedGeometry(null));
      dispatch(handleStateGridLatLng(null));

      // 🔁 Re-fetch fresh data WITHOUT grid filter
      if (selectedFilters?.levelOne) {
        onFilterUpdate();
      }
    }
  }, [gridId]); // keep deps minimal so it runs exactly when gridId changes

  const getStateRangeValue = (minValue: number, maxValue: number) => {
    return {
      minValue: formatNumber(minValue, 2),
      maxValue: formatNumber(maxValue, 2),
      minColor:
        activeView === ACTIVE_VIEW_COUNTRY
          ? COLOR.country.min
          : COLOR.state.min,
      maxColor:
        activeView === ACTIVE_VIEW_COUNTRY
          ? COLOR.country.max
          : COLOR.state.max,
    };
  };

  const isCountryStateView = () => {
    return !isGridViewSelected && stateFilter === COUNTRY;
  };
  const isSpecificStateGridView = () =>
    isGridViewSelected && stateFilter !== COUNTRY;

  const isSpecificStateStateView = () =>
    !isGridViewSelected && stateFilter !== COUNTRY;

  const isClaimsMode = useMemo(() => {
    const l1 = String(selectedFilters?.levelOne ?? "")
      .toLowerCase()
      .trim();
    const l2 = String(selectedFilters?.levelTwo ?? "")
      .toLowerCase()
      .trim();
    return (
      l1 === "claims" &&
      (l2 === "rejected" ||
        l2 === "repudiated" ||
        l2 === "cwp" ||
        l2 === "approved but payment not initiated" ||
        l2 === "claim paid" ||
        l2 === "payment initiated but not completed" ||
        l2 === "claim pending (before approval) - loss reserve")
    );
  }, [selectedFilters]);

  // detect "Policy" mode and compute when to disable state clicks
  const isPolicyMode = useMemo(() => {
    const l1 = String(selectedFilters?.levelOne ?? "")
      .toLowerCase()
      .trim();
    return l1 === "policy";
  }, [selectedFilters]);

  // detect "POSP" mode
  const isPospMode = useMemo(() => {
    const l1 = String(selectedFilters?.levelOne ?? "")
      .toLowerCase()
      .trim();
    return l1 === "pospm";
  }, [selectedFilters]);

  const disableStateClicks = useMemo(() => {
  return (
    isCountryStateView() &&
    (
      isPolicyMode ||
      isClaimsMode ||
      isPospMode ||
      isLossRatioMetric(
        selectedFilters?.levelOne,
        selectedFilters?.levelTwo
      )
    )
  );
}, [
  isPolicyMode,
  isClaimsMode,
  isPospMode,
  isGridViewSelected,
  stateFilter,
  selectedFilters,
]);

  const updateHeader = (
    levelOne: string,
    levelTwo: string,
    aggregated: any,
  ) => {
    setTotalPayoutUI(null);

    // if (!isCountryStateView() && isClaimsMode) {
    //   setAggregatedTitle(null);
    //   setAggregatedValue(null);
    //   setAverageCatchmentRatio(null);
    //   setColumnHeaders(getClaimsStateGridHeadersByLevel2(levelTwo));
    //   setTableTitle(`CLAIMS - ${String(levelTwo || "").toUpperCase()} DETAILS`);
    //   setLegendStyle(
    //     getStateRangeValue(
    //       legendValues?.min_claim_count,
    //       legendValues?.max_claim_count,
    //     ),
    //   );

    //   const aggPayout = Number(stateViewData?.aggregated?.total_payout);
    //   setTotalPayoutUI(Number.isFinite(aggPayout) ? aggPayout : null);
    //   return;
    // }

    const setLegendWithRound = (minRaw: any, maxRaw: any) => {
      const min = Number(minRaw ?? 0);
      const max = Number(maxRaw ?? 0);
      const safeMax = Math.max(max, min || 1); // avoid zero-width domain
      setLegendStyle({
        minValue: Math.round(min),
        maxValue: Math.round(safeMax),
        minColor: COLOR.state.min,
        maxColor: COLOR.state.max,
      });
    };
    
    const valueName = getValueKey(levelOne, levelTwo);
    const result = getStateIdWiseDataWithSum(
      stateViewData?.data,
      (isGridViewSelected || selectedFilters?.state !== COUNTRY) ? "gridid" : "sid",
      valueName,
      levelOne,
      levelTwo,
    );
    if (legendValues && result) {
      const keys = Object.keys(legendValues);
      const newLegendValues: Record<string, number> = {};
      if (keys.length) {
        keys.forEach((key) => {
          if (key.includes("min")) newLegendValues[key] = result?.min === result?.max ? result?.min - 1 :result?.min ?? 0;
          if (key.includes("max")) newLegendValues[key] = result?.max ?? 0;
        });
      }
      
      if(!isGridViewSelected) {
        setLegendStyle({
          minValue: Math.round(result?.min ?? 0),
          maxValue: Math.round(result?.max ?? 0),
          minColor: COLOR.state.min,
          maxColor: COLOR.state.max,
        });
      }
    }

    // ---- Existing non-claims logic ----
    switch (levelOne + "-" + levelTwo) {
      case COMBINED_LEVELS_FILTER.POSPm_POSP:
        setAggregatedTitle(AGGREGATED_TITLE.total_posps);
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_posps),
        );
        if (isCountryStateView()) {
          setColumnHeaders(getCountryStateTableHeader("POSP Count"));
        } else {
          setColumnHeaders(POSP_HEADERS);
        }
        setTableTitle("POSP'S DETAILS");
        setLegendWithRound(
          legendValues?.min_posp_count,
          legendValues?.max_posp_count,
        );
        break;

      case COMBINED_LEVELS_FILTER.POSPm_ARABLE:
        setAggregatedTitle(AGGREGATED_TITLE.total_arable_area + "(acres)");
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_arable_area),
        );
        setAverageCatchmentRatio(aggregated?.average_catchment_ratio);
        if (isCountryStateView())
          setColumnHeaders(getCountryStateTableHeader("Arable Area"));
        else setColumnHeaders(POSP_ARABLE_HEADERS);
        setTableTitle("ARABLE DETAILS");

        setLegendWithRound(
          legendValues?.min_catchment_ratio,
          legendValues?.max_catchment_ratio,
        );
        break;
      case COMBINED_LEVELS_FILTER.POSPm_TARGET: {
        setAggregatedTitle(AGGREGATED_TITLE.total_target_value);
        setAggregatedValue(
          numberFormatWithCurrency(aggregated?.total_target_value),
        );

        if (isCountryStateView())
          setColumnHeaders(getCountryStateTableHeader("Target Value"));
        else setColumnHeaders(POSP_TARGET_HEADERS);

        setTableTitle("TARGET DETAILS");

        // 1) Prefer backend-provided min/max if available
        let minTarget = Number(stateViewData?.min_max?.min_target_value);
        let maxTarget = Number(stateViewData?.min_max?.max_target_value);

        // 2) Fallback to legendValues already in store
        if (!Number.isFinite(minTarget) || !Number.isFinite(maxTarget)) {
          minTarget = Number(legendValues?.min_target_value);
          maxTarget = Number(legendValues?.max_target_value);
        }

        // 3) If still missing, derive from current response based on scope
        if (!Number.isFinite(minTarget) || !Number.isFinite(maxTarget)) {
          const rows = Array.isArray(stateViewData?.data)
            ? stateViewData.data
            : [];

          // helper: parse numeric including "12,000"
          const toNum = (v: any): number =>
            typeof v === "string"
              ? Number(v.replace(/,/g, "")) || 0
              : Number(v ?? 0) || 0;

          if (isCountryStateView()) {
            // COUNTRY VIEW → legend must reflect STATE TOTALS (same domain as table)
            const totalsByState: Record<string, number> = {};
            for (const r of rows) {
              const sid = String(r?.sid ?? "");
              if (!sid) continue;
              const num = toNum(r?.target_value);
              totalsByState[sid] = (totalsByState[sid] ?? 0) + num;
            }

            let min = Infinity;
            let max = -Infinity;
            for (const total of Object.values(totalsByState)) {
              if (total < min) min = total;
              if (total > max) max = total;
            }
            if (!Number.isFinite(min)) min = 0;
            if (!Number.isFinite(max)) max = Math.max(1, min);

            minTarget = min;
            maxTarget = max;

            // keep store in sync so Map coloring uses the same domain
            dispatch(
              updateLegendValue({
                min_target_value: minTarget,
                max_target_value: maxTarget,
              }),
            );
          } else {
            // STATE / GRID VIEW → per-row domain is fine
            let min = Infinity;
            let max = -Infinity;
            for (const r of rows) {
              const num = toNum(r?.target_value);
              if (num < min) min = num;
              if (num > max) max = num;
            }
            if (!Number.isFinite(min)) min = 0;
            if (!Number.isFinite(max)) max = Math.max(1, min);

            minTarget = min;
            maxTarget = max;

            dispatch(
              updateLegendValue({
                min_target_value: minTarget,
                max_target_value: maxTarget,
              }),
            );
          }
        }

        const safeMax = Math.max(maxTarget, minTarget || 1);

        setLegendStyle({
          minValue: minTarget,
          maxValue: safeMax,
          minColor: COLOR.state.min,
          maxColor: COLOR.state.max,
        });

        break;
      }
      case COMBINED_LEVELS_FILTER.POSPm_LOCATION:
        setAggregatedTitle("");
        setAggregatedValue("");
        setAverageCatchmentRatio("");
        setColumnHeaders(POSP_LOCATION);
        setTableTitle("POSP DETAILS");
        setLegendStyle(null);
        break;

      case COMBINED_LEVELS_FILTER.Policy_Customer_Count:
        setAggregatedTitle(AGGREGATED_TITLE.total_customers);
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_customers),
        );
        if (isCountryStateView())
          setColumnHeaders(getCountryStateTableHeader("Customer Count"));
        else setColumnHeaders(CUSTOMER_COUNT_HEADERS);
        setTableTitle("CUSTOMER COUNT DETAILS");
        setLegendWithRound(
          legendValues?.min_customers,
          legendValues?.max_customers,
        );
        break;

      case COMBINED_LEVELS_FILTER.Policy_Policy_Count:
        setAggregatedTitle(AGGREGATED_TITLE.total_policies);
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_policies),
        );
        if (isCountryStateView())
          setColumnHeaders(getCountryStateTableHeader("Policy Count"));
        else setColumnHeaders(POLICIES_HEADERS);
        setTableTitle("POLICIES DETAILS");
        setLegendWithRound(
          legendValues?.min_policies,
          legendValues?.max_policies,
        );
        break;

      case COMBINED_LEVELS_FILTER.Policy_Premium:
        setAggregatedTitle(AGGREGATED_TITLE.total_premium);
        setAggregatedValue(numberFormatWithCurrency(aggregated?.total_premium));
        if (isCountryStateView())
          setColumnHeaders(getCountryStateTableHeader("Premium"));
        else setColumnHeaders(PREMIUM_HEADERS);
        setTableTitle("PREMIUM DETAILS");
        setLegendWithRound(
          legendValues?.min_premium,
          legendValues?.max_premium,
        );
        break;

      case COMBINED_LEVELS_FILTER.Policy_Sum_Insured:
        setAggregatedTitle(AGGREGATED_TITLE.total_suminsured);
        setAggregatedValue(
          numberFormatWithCurrency(aggregated?.total_suminsured),
        );
        if (isCountryStateView())
          setColumnHeaders(getCountryStateTableHeader("Sum Insured"));
        else setColumnHeaders(SUM_INSURED_HEADERS);
        setTableTitle("SUM INSURED DETAILS");
        setLegendWithRound(
          legendValues?.min_suminsured,
          legendValues?.max_suminsured,
        );
        break;

      case COMBINED_LEVELS_FILTER.Policy_Insured_Area:
        setAggregatedTitle(AGGREGATED_TITLE.total_insured_area + "(acres)");
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_insured_area),
        );
        if (isCountryStateView())
          setColumnHeaders(getCountryStateTableHeader("Insured Area"));
        else setColumnHeaders(INSURED_AREA_HEADER);
        setTableTitle("INSURED AREA DETAILS");
        setLegendWithRound(legendValues?.min_area, legendValues?.max_area);
        break;

      case COMBINED_LEVELS_FILTER.Claims_Claim_Processed:
        setAggregatedTitle(AGGREGATED_TITLE.total_claims);
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_claims),
        );
        if (isCountryStateView())
          setColumnHeaders(getCountryStateTableHeader("Claim Processed"));
        else setColumnHeaders(CLAIMS_CLAIM_PROCESSED_HEADERS);
        setTableTitle("CLAIM PROCESSED DETAILS");
        setLegendStyle(
          getStateRangeValue(
            legendValues?.min_claim_count,
            legendValues?.max_claim_count,
          ),
        );

        setTotalPayoutUI(
          Number.isFinite(Number(stateViewData?.aggregated?.total_payout))
            ? Number(stateViewData?.aggregated?.total_payout)
            : null,
        );
        break;

      case COMBINED_LEVELS_FILTER.Claims_Claim_Pending:
        setAggregatedTitle(AGGREGATED_TITLE.total_claims);
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_claims),
        );
        if (isCountryStateView())
          setColumnHeaders(getCountryStateTableHeader("Claim Pending"));
        else setColumnHeaders(CLAIMS_CLAIM_PROCESSED_HEADERS);
        setTableTitle("CLAIM PENDING DETAILS");
        setLegendStyle(
          getStateRangeValue(
            legendValues?.min_claim_count,
            legendValues?.max_claim_count,
          ),
        );
        setTotalPayoutUI(
          Number.isFinite(Number(stateViewData?.aggregated?.total_payout))
            ? Number(stateViewData?.aggregated?.total_payout)
            : null,
        );
        break;

      case COMBINED_LEVELS_FILTER.Claims_Customer_Count:
        if (isCountryStateView())
          setColumnHeaders(getCountryStateTableHeader("Customer Count"));
        else setColumnHeaders(CLAIMS_CUSTOMER_COUNT_HEADERS);
        setTableTitle("CUSTOMER COUNT DETAILS");
        setLegendStyle(
          getStateRangeValue(
            legendValues?.min_customer_count,
            legendValues?.max_customer_count,
          ),
        );
        setTotalPayoutUI(
          Number.isFinite(Number(stateViewData?.aggregated?.total_payout))
            ? Number(stateViewData?.aggregated?.total_payout)
            : null,
        );
        break;

      case COMBINED_LEVELS_FILTER.Claims_Amount_Disbursed:
        setAggregatedTitle(AGGREGATED_TITLE.total_payout);
        setAggregatedValue(numberFormatWithCurrency(aggregated?.total_payout));
        if (isCountryStateView())
          setColumnHeaders(getCountryStateTableHeader("Amount Disbursed"));
        else setColumnHeaders(CLAIMS_AMOUNT_DISBURSED_HEADERS);
        setTableTitle("AMOUNT DISBURSED DETAILS");
        setTotalPayoutUI(
          Number.isFinite(Number(stateViewData?.aggregated?.total_payout))
            ? Number(stateViewData?.aggregated?.total_payout)
            : null,
        );
        break;

      case COMBINED_LEVELS_FILTER.Claims_Amount_Can_Be_Disbursed:
        setAggregatedTitle(AGGREGATED_TITLE.total_payout);
        setAggregatedValue(numberFormatWithCurrency(aggregated?.total_payout));
        if (isCountryStateView())
          setColumnHeaders(
            getCountryStateTableHeader("Amount Can Be Disbursed"),
          );
        else setColumnHeaders(CLAIMS_AMOUNT_DISBURSED_HEADERS);
        setTableTitle("AMOUNT CAN BE DISBURSED DETAILS");
        setTotalPayoutUI(
          Number.isFinite(Number(stateViewData?.aggregated?.total_payout))
            ? Number(stateViewData?.aggregated?.total_payout)
            : null,
        );
        break;

      case COMBINED_LEVELS_FILTER.Claims_Loss_Ratio:
        setAggregatedTitle(AGGREGATED_TITLE.average_loss_ratio);
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.average_loss_ratio),
        );
        if (isCountryStateView())
          setColumnHeaders(getCountryStateTableHeader("Loss Ratio"));
        else setColumnHeaders(CLAIMS_CLAIM_RADIO_HEADERS);
        setTableTitle("LOSS RATIO(CLAIM/PREMIUM) DETAILS");
        setTotalPayoutUI(
          Number.isFinite(Number(stateViewData?.aggregated?.total_payout))
            ? Number(stateViewData?.aggregated?.total_payout)
            : null,
        );
        break;

      case COMBINED_LEVELS_FILTER.Claims_Total_Claim_Count:
        setAggregatedTitle(AGGREGATED_TITLE.total_claims);
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_claims),
        );
        if (isCountryStateView())
          setColumnHeaders(getCountryStateTableHeader("Claim_Count"));
        else setColumnHeaders(CLAIMS_CLAIM_COUNT_HEADERS);
        setTableTitle("TOTAL CLAIM COUNT DETAILS");
        setLegendStyle(
          getStateRangeValue(
            legendValues?.min_claim_count,
            legendValues?.max_claim_count,
          ),
        );
        setTotalPayoutUI(
          Number.isFinite(Number(stateViewData?.aggregated?.total_payout))
            ? Number(stateViewData?.aggregated?.total_payout)
            : null,
        );
        break;

      case COMBINED_LEVELS_FILTER.Claims_Rejected:
        setAggregatedTitle(AGGREGATED_TITLE.total_claims);
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_claims),
        );
        if (isCountryStateView())
          setColumnHeaders(CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS);
        else setColumnHeaders(CLAIMS_STATE_GRID_HEADERS_PAID);
        setTableTitle("REJECTED DETAILS");
        setLegendStyle({
          minValue: 0,
          maxValue: 0,
          minColor: COLOR.state.min,
          maxColor: COLOR.state.max,
        });
        setTotalPayoutUI(
          Number.isFinite(Number(stateViewData?.aggregated?.total_payout))
            ? Number(stateViewData?.aggregated?.total_payout)
            : null,
        );

        break;

      case COMBINED_LEVELS_FILTER.Claims_Repudiated:
        setAggregatedTitle(AGGREGATED_TITLE.total_claims);
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_claims),
        );
        if (isCountryStateView())
          setColumnHeaders(CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS);
        else setColumnHeaders(CLAIMS_STATE_GRID_HEADERS_PAID);
        setTableTitle("REPUDIATED DETAILS");
        setLegendStyle({
          minValue: 0,
          maxValue: 0,
          minColor: COLOR.state.min,
          maxColor: COLOR.state.max,
        });
        setTotalPayoutUI(
          Number.isFinite(Number(stateViewData?.aggregated?.total_payout))
            ? Number(stateViewData?.aggregated?.total_payout)
            : null,
        );

        break;

      case COMBINED_LEVELS_FILTER.Claims_Cwp:
        setAggregatedTitle(AGGREGATED_TITLE.total_claims);
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_claims),
        );
        if (isCountryStateView())
          setColumnHeaders(CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS);
        else setColumnHeaders(CLAIMS_STATE_GRID_HEADERS_PAID);
        setTableTitle("CWP DETAILS");
        setLegendStyle({
          minValue: 0,
          maxValue: 0,
          minColor: COLOR.state.min,
          maxColor: COLOR.state.max,
        });
        setTotalPayoutUI(
          Number.isFinite(Number(stateViewData?.aggregated?.total_payout))
            ? Number(stateViewData?.aggregated?.total_payout)
            : null,
        );

        break;

      case COMBINED_LEVELS_FILTER.Claims_Paid:
        setAggregatedTitle(AGGREGATED_TITLE.total_claims);
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_claims),
        );
        if (isCountryStateView())
          setColumnHeaders(CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_CP);
        else setColumnHeaders(CLAIMS_STATE_GRID_HEADERS_CP);
        setTableTitle("CLAIM PAID DETAILS");

        setTotalPayoutUI(
          Number.isFinite(Number(stateViewData?.aggregated?.total_payout))
            ? Number(stateViewData?.aggregated?.total_payout)
            : null,
        );
        break;

      case COMBINED_LEVELS_FILTER.Claims_Payment_Not_Initiated:
        setAggregatedTitle(AGGREGATED_TITLE.total_claims);
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_claims),
        );
        if (isCountryStateView())
          setColumnHeaders(CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_ABNP);
        else setColumnHeaders(CLAIMS_STATE_GRID_HEADERS_ABNP);
        setTableTitle("APPROVED BUT PAYMENT NOT INITIATED DETAILS");
        setTotalPayoutUI(
          Number.isFinite(Number(stateViewData?.aggregated?.total_payout))
            ? Number(stateViewData?.aggregated?.total_payout)
            : null,
        );
        break;

      case COMBINED_LEVELS_FILTER.Claims_Payment_Initiated:
        setAggregatedTitle(AGGREGATED_TITLE.total_claims);
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_claims),
        );
        if (isCountryStateView())
          setColumnHeaders(CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_ABNP);
        else setColumnHeaders(CLAIMS_STATE_GRID_HEADERS_ABNP);
        setTableTitle("PAYMENT INITIATED BUT NOT COMPLETED DETAILS");
        setTotalPayoutUI(
          Number.isFinite(Number(stateViewData?.aggregated?.total_payout))
            ? Number(stateViewData?.aggregated?.total_payout)
            : null,
        );
        break;

      case COMBINED_LEVELS_FILTER.Claims_Loss_Reserve:
        setAggregatedTitle(AGGREGATED_TITLE.total_claims);
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_claims),
        );
        if (isCountryStateView())
          setColumnHeaders(CLAIMS_COUNTRY_STATE_COUNT_AMOUNT_HEADERS_LS);
        else setColumnHeaders(CLAIMS_STATE_GRID_HEADERS_LS);
        setTableTitle("CLAIM PENDING (BEFORE APPROVAL) - LOSS RESERVE DETAILS");

        const l2 = selectedFilters?.levelTwo;

        if (isLossReserveL2(l2)) {
          const aggReserve = Number(
            stateViewData?.aggregated?.total_loss_reserve,
          );
          if (Number.isFinite(aggReserve) && aggReserve >= 0) {
            setTotalPayoutUI(aggReserve);
          } else {
            setTotalPayoutUI(
              computeTotalReserveFromRows(stateViewData?.data ?? []),
            );
          }
        } else {
          const aggPayout = Number(stateViewData?.aggregated?.total_payout);
          if (Number.isFinite(aggPayout)) {
            setTotalPayoutUI(aggPayout);
          } else {
            setTotalPayoutUI(
              computeTotalPayoutFromRows(stateViewData?.data ?? []),
            );
          }
        }
        return;
      default:
        setColumnHeaders([]);
        setTableTitle("");
        setAggregatedTitle(null);
        setAggregatedValue(null);

        setTotalPayoutUI(null);
    }
  };

  // at top of component: you already have `statesWithId` in scope
  const getStateNameBySid = (sid?: any): string | undefined => {
    if (!sid) return undefined;
    const rec = getDataWithKeyValue(statesWithId, "sid", sid);
    return rec?.vstate; // or `rec?.state_name` depending on your structure
  };

  const aggregateByState = (rows: any[]) => {
    const map: Record<
      string,
      { vstate: string; claims_count: number; amount: number }
    > = {};
    rows.forEach((r) => {
      // derive robust state name
      const derivedName = r?.vstate || getStateNameBySid(r?.sid) || "Unknown";

      if (!map[derivedName]) {
        map[derivedName] = { vstate: derivedName, claims_count: 0, amount: 0 };
      }
      map[derivedName].claims_count += Number(r?.claims_count ?? 0);
      map[derivedName].amount += Number(r?.amount ?? 0);
    });
    return Object.values(map);
  };

  const updateTableData = (keyValue?: string, keyName?: string) => {
    const levelTwo = String(selectedFilters?.levelTwo ?? "")
      .toLowerCase()
      .trim();
    const levelOne = selectedFilters?.levelOne;
    const dataArray = stateViewData?.data;

    if (isClaimsMode && Array.isArray(dataArray)) {
      const rows = dataArray.map((r: any) => {
        const paidFromArray = Array.isArray(r?.payout_amounts)
          ? r.payout_amounts.reduce(
              (sum: number, v: any) => sum + (Number(v) || 0),
              0,
            )
          : 0;

        const paid = Number(r?.amount_paid ?? paidFromArray ?? 0);
        const sanctioned = Number(
          r?.amount_sanctioned ?? r?.approved_amount ?? 0,
        );
        const outstanding = Number(r?.outstanding ?? 0);
        const toBePaid = Number(r?.amount_to_be_paid ?? 0);
        const computedOutstanding = Math.max(sanctioned - paid, 0);

        // NEW: sum net_payout from nested claim_details for ABPNI rows
        const netPayoutFromDetails = Array.isArray(r?.claims?.claim_details)
          ? r.claims.claim_details.reduce(
              (s: number, d: any) => s + (Number(d?.net_payout) || 0),
              0,
            )
          : 0;

        const amount = isLossReserveL2(levelTwo)
          ? Array.isArray(r?.loss_reserve_values)
            ? r.loss_reserve_values.reduce(
                (s: number, v: any) => s + (Number(v) || 0),
                0,
              )
            : 0
          : levelTwo === "approved but payment not initiated"
            ? netPayoutFromDetails
            : levelTwo === "approved but not paid"
              ? toBePaid || outstanding || computedOutstanding || paid
              : paidFromArray || paid || 0;
        return {
          ...r,
          amount: Number.isFinite(Number(amount)) ? Number(amount) : 0,
          claims_count: Number(r?.claims_count ?? 0),
          gridid: r?.gridid,
          claim_ids: Array.isArray(r?.claim_ids)
            ? r.claim_ids
            : r?.claim_ids
              ? [r?.claim_ids]
              : [],
          area: Number(r?.area ?? 0),
        };
      });

      const itemArray = parseStateData(
        rows,
        levelOne,
        levelTwo,
        keyValue,
        keyName,
      );
      if (isCountryStateView()) {
        const aggregatedRows = aggregateByState(itemArray);
        setStateTableData(
          sortListItems(aggregatedRows, "claims_count", TABLE_SORT_DESC),
        );
        setSortBy("claims_count");
        setSortOrder(TABLE_SORT_DESC);
      } else {
        setStateTableData(sortListItems(itemArray, "gridid", TABLE_SORT_ASC));
        setSortBy("gridid");
        setSortOrder(TABLE_SORT_ASC);
      }

      if (isLossReserveL2(levelTwo)) {
        setTotalPayoutUI(computeTotalReserveFromRows(itemArray));
      } else {
        setTotalPayoutUI(computeTotalPayoutFromRows(itemArray));
      }
      return;
    }

    const itemArray = parseStateData(
      dataArray,
      levelOne,
      levelTwo,
      keyValue,
      keyName,
    );
    if (
      levelOne + "-" + String(selectedFilters?.levelTwo ?? "") ===
      COMBINED_LEVELS_FILTER.POSPm_LOCATION
    ) {
      dispatch(
        handleStoreMarkers({ marker: MARKER_TYPE.POSPS, res: itemArray }),
      );
      if (!markerType.includes(MARKER_TYPE.POSPS)) {
        setMarkersType([MARKER_TYPE.POSPS]);
      }
    }
    setStateTableData(sortListItems(itemArray, "gridid", TABLE_SORT_ASC));
    setSortBy("gridid");
    setSortOrder(TABLE_SORT_ASC);

    const aggPayout = Number(stateViewData?.aggregated?.total_payout);
    setTotalPayoutUI(Number.isFinite(aggPayout) ? aggPayout : null);
  };

  useEffect(() => {
    const levelTwo = selectedFilters?.levelTwo;
    const levelOne = selectedFilters?.levelOne;
    updateHeader(levelOne, levelTwo, stateViewData?.aggregated);
  }, [legendValues, selectedFilters, stateViewData?.aggregated]);

  useEffect(() => {
    setIsLoading(false);
    setAggregatedTitle(null);
    setAggregatedValue(null);
    setAverageCatchmentRatio(null);

    setTotalPayoutUI(null);

    if (
      stateViewData?.data &&
      isCountryStateView() &&
      selectedFilters?.levelTwo !== REAL_TIME_LOCATION
    ) {
      const levelOne = selectedFilters?.levelOne;
      const levelTwo = selectedFilters?.levelTwo;

      if (isClaimsMode) {
        const levelOne = selectedFilters?.levelOne;
        const levelTwo = selectedFilters?.levelTwo;
        const valueName = getValueKey(levelOne, levelTwo);
        const result = getStateIdWiseDataWithSum(
          stateViewData?.data,
          (isGridViewSelected || selectedFilters?.state !== COUNTRY) ? "gridid" : "sid",
          valueName,
          levelOne,
          levelTwo,
        );
        if (legendValues && result) {
          const keys = Object.keys(legendValues);
          const newLegendValues: Record<string, number> = {};
          if (keys.length) {
            keys.forEach((key) => {
              if (key.includes("min")) newLegendValues[key] = result?.min === result?.max ? result?.min - 1 :result?.min ?? 0;
              if (key.includes("max")) newLegendValues[key] = result?.max ?? 0;
            });
          }
          dispatch(updateLegendValue(newLegendValues));
        }
        setIdWiseData(result?.data);
        updateTableData();

        const l2 = selectedFilters?.levelTwo;

        if (isLossReserveL2(l2)) {
          const aggReserve = Number(
            stateViewData?.aggregated?.total_loss_reserve,
          );
          if (Number.isFinite(aggReserve) && aggReserve >= 0) {
            setTotalPayoutUI(aggReserve);
          } else {
            setTotalPayoutUI(
              computeTotalReserveFromRows(stateViewData?.data ?? []),
            );
          }
        } else {
          const aggPayout = Number(stateViewData?.aggregated?.total_payout);
          if (Number.isFinite(aggPayout)) {
            setTotalPayoutUI(aggPayout);
          } else {
            setTotalPayoutUI(
              computeTotalPayoutFromRows(stateViewData?.data ?? []),
            );
          }
        }
        return;
      }

      const valueName = getValueKey(levelOne, levelTwo);
      const result = getStateIdWiseDataWithSum(
        stateViewData?.data,
        (isGridViewSelected || selectedFilters?.state !== COUNTRY) ? "gridid" : "sid",
        valueName,
        levelOne,
        levelTwo,
      );
      setIdWiseData(result?.data);

      if (legendValues && result) {
        const keys = Object.keys(legendValues);
        const newLegendValues: Record<string, number> = {};
        if (keys.length) {
          keys.forEach((key) => {
            if (key.includes("min")) newLegendValues[key] = result?.min ?? 0;
            if (key.includes("max")) newLegendValues[key] = result?.max ?? 0;
          });
        }
        dispatch(updateLegendValue(newLegendValues));
      }
      setStateTableData(result?.array ?? []);
      setSortBy("total");
      setSortOrder(TABLE_SORT_ASC);

      const aggPayout = Number(stateViewData?.aggregated?.total_payout);
      if (Number.isFinite(aggPayout)) {
        setTotalPayoutUI(aggPayout);
      } else if (isClaimsMode) {
        setTotalPayoutUI(computeTotalPayoutFromRows(stateViewData?.data ?? []));
      }
    } else if (stateViewData?.data) {
      setIdWiseData(
        getGridIdWiseData(
          stateViewData?.data ?? [],
          selectedFilters?.levelOne,
          selectedFilters?.levelTwo,
        ),
      );
      
      const levelOne = selectedFilters?.levelOne;
      const levelTwo = selectedFilters?.levelTwo;
      const valueName = getValueKey(levelOne, levelTwo);
      const result = getStateIdWiseDataWithSum(
        stateViewData?.data,
        "gridid",
        valueName,
        levelOne,
        levelTwo,
      );
      
      if (legendValues && result) {
        const keys = Object.keys(legendValues);
        const newLegendValues: Record<string, number> = {};
        if (keys.length) {
          keys.forEach((key) => {
            if (key.includes("min")) newLegendValues[key] = result?.min === result?.max ? result?.min - 1 :result?.min ?? 0;
            if (key.includes("max")) newLegendValues[key] = result?.max ?? 0;
          });
        }
        dispatch(updateLegendValue(newLegendValues));
      }
      
      setLegendStyle({
        minValue: Math.round(result?.min ?? 0),
        maxValue: Math.round(result?.max ?? 0),
        minColor: COLOR.state.min,
        maxColor: COLOR.state.max,
      });
      updateTableData();
    } else {
      setIdWiseData(null);
      setStateTableData(null);
      setTotalPayoutUI(null);
    }
  }, [stateViewData]);

  const onFilterUpdate = () => {
    setCanShowToolTip(true);
    if (!selectedFilters?.gridNumber) {
      setMapClickedInfo(null);
    }
    let _selectedState: any = null;
    let _selectedDistrict: any = null;
    let selectedProduct: any = null;
    const seasonArray: string[] = [];

    if (selectedFilters?.state) {
      _selectedState =
        selectedFilters?.state === COUNTRY
          ? selectedFilters?.state
          : getDataWithKeyValue(statesWithId, "vstate", selectedFilters?.state);
    }
    setSelectedState(_selectedState);

    if (selectedFilters?.district) {
      _selectedDistrict = getDataWithKeyValue(
        districtsWithId,
        "vdistrict",
        selectedFilters?.district,
      );
    }
    setSelectedDistrict(_selectedDistrict);

    if (selectedFilters?.product && selectedFilters?.product === ALL_PRODUCT) {
      selectedProduct = { productid: "", productname: "" };
    } else if (selectedFilters?.product) {
      selectedProduct = getDataWithKeyValue(
        productListWithId,
        "productname",
        selectedFilters?.product,
      );
    }

    if (selectedFilters?.season && selectedFilters?.season?.length > 0) {
      selectedFilters?.season?.forEach((item: OptionType) => {
        seasonArray.push(item?.value);
      });
    }

    if (_selectedState?.sid || _selectedState === COUNTRY) {
      getStateViewData(
        selectedProduct?.productid,
        seasonArray,
        selectedFilters?.year,
        _selectedState?.sid ? _selectedState?.sid : "",
        _selectedDistrict?.did,
      );
    }
    dispatch(handleStateSelectedGeometry(null));
  };

  useEffect(() => {
    dispatch(handleStateGridLatLng(null));
    if (selectedFilters?.levelOne) {
      onFilterUpdate();
    }
  }, [selectedFilters, isGridViewSelected]);

  const getStateViewData = (
    productId: string | number,
    seasonArray: string[],
    season_year: string | number,
    stateId: string | number,
    districtId: string | number,
  ) => {
    setIsLoading(true);
    setStateTableData(null);
    setIdWiseData(null);
    setAggregatedTitle(null);
    setAggregatedValue(null);
    setAverageCatchmentRatio(null);
    setTotalPayoutUI(null);
    setLegendStyle(null);
    const params: any = getRequestParams(
      selectedFilters,
      productId,
      seasonArray,
      season_year,
      stateId,
      districtId,
      gridId,
      mapClickedInfo,
    );

    const isCountry = selectedFilters?.state === COUNTRY;
    if (isCountry) {
      delete params.state;
      params.country = normalizeCountry(
        selectedFilters?.countryCode ??
          selectedFilters?.country?.code ??
          selectedFilters?.country,
      );
    } else {
      if (
        params.state === "All" ||
        params.state === "" ||
        params.state == null
      ) {
        delete params.state;
      }
    }

    const l1 = String(selectedFilters?.levelOne ?? "")
      .toLowerCase()
      .trim();
    const l2 = String(selectedFilters?.levelTwo ?? "")
      .toLowerCase()
      .trim();
    if (l1 === "claims" && l2) {
      params.metric = Level2KeyMap[l2] ?? l2;
    }

    params.__scope = isCountry ? "country" : "state";
    params.__reqId = Date.now().toString();

    dispatch(getStateData(params));
  };

  const sortTableItems = (sortBy: string, sortOrder: string) => {
    setStateTableData(sortListItems(stateTableData ?? [], sortBy, sortOrder));
  };

  const onRowClick = (rowItem: any) => {
    // NOTE: For Country + State view the existing condition already prevents drilling into grid.
    if (
      (stateFilter === COUNTRY && isGridViewSelected) ||
      stateFilter !== COUNTRY
    ) {
      setGridId(rowItem?.gridid);
      updateTableData(rowItem?.gridid, "gridid");
      dispatch(getGridLatLng({ gridid: rowItem?.gridid }, ACTIVE_VIEW_STATE));
    }
  };

  // useEffect(() => {
  //   console.log(stateTableData, "ss");
  // }, [stateTableData]);

  const isLossReserve =
    `${selectedFilters?.levelOne}-${selectedFilters?.levelTwo}` ===
    COMBINED_LEVELS_FILTER.Claims_Loss_Reserve;

  return (
    <div className="results-container">
      <div className="view-header mb-2">
        <span>
          {selectedFilters?.state !== COUNTRY
            ? `State Level View ${selectedFilters?.state && "- " + selectedFilters.state}`
            : "Country Level View"}
        </span>
      </div>

      {canShowClaimsNote && <div className="claims-note">{claimsNoteRaw}</div>}

      <div className="map-table">
        {stateFilter === COUNTRY && selectedFilters?.levelOne !== "Weather" && (
          <div className="form-switch switch d-flex justify-content-center ">
            <label className="form-check-label">View: </label>
            <label className="form-check-label switch-text ms-1">State</label>
            <input
              className="form-check-input ms-1"
              type="checkbox"
              id="flexSwitchCheckDefault"
              checked={isGridViewSelected}
              onChange={() => {
                setGridViewSelected(!isGridViewSelected);
              }}
            />
            <label className="form-check-label switch-text ms-2">Grid</label>
          </div>
        )}

        {selectedFilters?.levelOne === "Weather" && (
          <>
            <div className="forecast-dropdown-layer">
              <WeatherLayerDropdown
                title="Weather Layer"
                items={items}
                selectedId={selected}
                onChange={() => setSelected("precipitation")}
              />
            </div>
            <div className="forecast-date-layer pt-2">
              <DateHourMinutePlayer
                today={moment().format(DATE_FORMAT.YYYY_MM_DD)}
                value={forecastDate}
                onChange={setForecastDate}
                stepMinutes={15}
                intervalMs={1000}
                loop={false}
                size="md"
              />
            </div>
          </>
        )}

        <div className="map">
          <Map
            activeView={activeView}
            markerType={markerType}
            markerData={markersStore}
            idWiseData={idWiseData}
            setGridId={setGridId}
            mapView={
              isGridViewSelected ? MAP_VIEW.GRID_WISE : MAP_VIEW.STATE_WISE
            }
            filterName={
              selectedFilters?.levelOne + "-" + selectedFilters?.levelTwo
            }
            legendValues={legendValues}
            legendStyle={legendStyle}
            distance={Number(distanceRange ?? 0)}
            district={selectedDistrict}
            state={selectedState}
            mapClickedInfo={mapClickedInfo}
            setMapClickedInfo={(mapInfo) => {
              // ignore state clicks when disabled (Country + State view + Policy/Claims/POSPm)
              if (disableStateClicks) return;
              dispatch(handleCountryGridLatLng(null));
              setMapClickedInfo(mapInfo);
            }}
            selectedGeometry={selectedGeometry}
            gridCenter={
              gridLatLng?.properties
                ? [
                    Number(gridLatLng?.properties?.lat_cen),
                    Number(gridLatLng?.properties?.lon_cen),
                  ]
                : null
            }
            onMarkerClick={(markerData, keyName) => {
              // ignore state markers when disabled
              if (disableStateClicks) return;
              setGridId(markerData?.gridid);
              updateTableData(markerData[keyName], keyName);
            }}
            selectedFilters={selectedFilters}
            computedDate={computedDate}
            forecastDate={
              forecastDate.format(DATE_FORMAT.YYYY_MM_DD) ??
              moment().format(DATE_FORMAT.YYYY_MM_DD)
            }
            
            disableStateClicks={disableStateClicks}
          />
        </div>

        {stateTableData?.length > 0 && (
          <div className="result-table">
            <div className="table-header">
              <p className="view-header">{tableTitle}</p>
              <p className="view-header">
                {aggregatedTitle && (
                  <>
                    {aggregatedTitle}: <b>{aggregatedValue}</b>
                  </>
                )}
              </p>
              {}
              <p className="view-header">
                {totalPayoutUI != null && (
                  <>
                    {isLossReserve
                      ? "Total Reserve Amount: "
                      : "Total Payout: "}
                    <b>{numberFormatWithCurrency(totalPayoutUI)}</b>
                  </>
                )}
              </p>
              {showCount && (
                <p className="view-header">
                  Total Count:{" "}
                  <b>
                    {Array.isArray(stateTableData) ? stateTableData.length : 0}
                  </b>
                </p>
              )}
            </div>

            <div className="table-header">
              <p className="view-header" />
              <p className="view-header">
                {averageCatchmentRatio && (
                  <>
                    Average Catchment Ratio:{" "}
                    <b>{Number(averageCatchmentRatio).toFixed(2)}</b>
                  </>
                )}
              </p>
              <p className="view-header" />
            </div>

            <TableComponent
              data={stateTableData}
              columnHeaders={columnHeaders}
              onAction={onRowClick}
              handleSort={sortTableItems}
              sortBy={sortBy}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              setSortBy={setSortBy}
              filterName=""
            />
          </div>
        )}

        {stateTableData && stateTableData?.length === 0 && !isLoading && (
          <div className="d-flex justify-content-center not-found">
            {stateFilter === COUNTRY && !isGridViewSelected && (
              <> There is no business in state.</>
            )}
            {(stateFilter !== COUNTRY || isGridViewSelected) && (
              <> There is no business in grid.</>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StateMap;
